import { clamp, roundTo } from '../units/round.js';

/**
 * Readiness (docs/TRAINING_ENGINE.md §6).
 * Escalas 1-5 con extremos etiquetados en la UI: energia y motivacion 5 = mejor;
 * estres y agujetas 5 = maximo (peor). El motor invierte el signo aqui.
 */
export const READINESS_SCORE_VERSION = 'readiness_v1';

export interface ReadinessInput {
  sleepHours: number;
  sleepQuality: number;
  energy: number;
  motivation: number;
  stress: number;
  soreness: number;
}

export const READINESS_WEIGHTS = {
  sleepHours: 0.25,
  sleepQuality: 0.15,
  energy: 0.2,
  motivation: 0.1,
  stress: 0.15,
  soreness: 0.15,
} as const;

/** Rampa del sueño: <5 h = 0; 7-9 h = 1; por encima de 9,5 h baja un poco. */
export function normalizeSleepHours(hours: number): number {
  if (hours <= 5) return 0;
  if (hours < 7) return (hours - 5) / 2;
  if (hours <= 9) return 1;
  if (hours <= 9.5) return 0.95;
  return 0.9;
}

function normalizeScale(value: number): number {
  return clamp((value - 1) / 4, 0, 1);
}

function invertScale(value: number): number {
  return clamp((5 - value) / 4, 0, 1);
}

export function computeReadiness(input: ReadinessInput): number {
  const parts =
    READINESS_WEIGHTS.sleepHours * normalizeSleepHours(input.sleepHours) +
    READINESS_WEIGHTS.sleepQuality * normalizeScale(input.sleepQuality) +
    READINESS_WEIGHTS.energy * normalizeScale(input.energy) +
    READINESS_WEIGHTS.motivation * normalizeScale(input.motivation) +
    READINESS_WEIGHTS.stress * invertScale(input.stress) +
    READINESS_WEIGHTS.soreness * invertScale(input.soreness);

  return roundTo(parts * 100, 1);
}

export interface SessionModulation {
  /** Factor de carga para toda la sesion. */
  loadFactor: number;
  /** Incremento del RIR objetivo. */
  rirDelta: number;
  /** Factor de volumen (1 = sin cambio). */
  volumeFactor: number;
  /** Sugerencias no automaticas para la UI. */
  reasonCodes: string[];
}

/**
 * Modulacion de la SESION DE HOY (estado puntual).
 * Una sola mala noche nunca produce un cambio estructural: para tocar la
 * planificacion hace falta tendencia sostenida (docs/TRAINING_ENGINE.md §6.2).
 */
export function sessionModulation(readinessScore: number | null): SessionModulation {
  if (readinessScore === null) {
    return { loadFactor: 1, rirDelta: 0, volumeFactor: 1, reasonCodes: ['no_checkin'] };
  }
  if (readinessScore >= 80) {
    return { loadFactor: 1, rirDelta: 0, volumeFactor: 1, reasonCodes: ['readiness_high'] };
  }
  if (readinessScore >= 65) {
    return { loadFactor: 1, rirDelta: 0, volumeFactor: 1, reasonCodes: ['readiness_normal'] };
  }
  if (readinessScore >= 50) {
    return { loadFactor: 0.95, rirDelta: 0.5, volumeFactor: 1, reasonCodes: ['readiness_reduced'] };
  }
  if (readinessScore >= 35) {
    return {
      loadFactor: 0.9,
      rirDelta: 1,
      volumeFactor: 0.9,
      reasonCodes: ['readiness_low', 'prefer_stable_variants'],
    };
  }
  return {
    loadFactor: 0.85,
    rirDelta: 1.5,
    volumeFactor: 0.8,
    reasonCodes: ['readiness_very_low', 'consider_moving_session'],
  };
}

/**
 * Agujetas altas en una region concreta reducen su volumen aunque el readiness
 * global sea bueno (docs/TRAINING_ENGINE.md §6.3).
 */
export function regionVolumeFactor(regionSoreness: number | null): number {
  return regionSoreness !== null && regionSoreness >= 4 ? 0.7 : 1;
}

export interface ReadinessTrend {
  mean7d: number | null;
  mean28d: number | null;
  /** true solo si la caida se sostiene: no basta un dia malo. */
  sustainedDrop: boolean;
}

export function readinessTrend(recentScores: readonly { at: string; score: number }[]): ReadinessTrend {
  if (recentScores.length === 0) return { mean7d: null, mean28d: null, sustainedDrop: false };

  const sorted = [...recentScores].sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
  const newest = Date.parse(sorted[0]?.at ?? '');
  const within = (days: number): number[] =>
    sorted
      .filter((entry) => newest - Date.parse(entry.at) <= days * 24 * 3600 * 1000)
      .map((entry) => entry.score);

  const last7 = within(7);
  const last28 = within(28);
  const mean = (values: number[]): number | null =>
    values.length === 0 ? null : roundTo(values.reduce((a, b) => a + b, 0) / values.length, 1);

  const mean7d = mean(last7);
  const mean28d = mean(last28);

  // Cinco dias seguidos por debajo del umbral: eso ya es tendencia.
  const lowStreak = sorted.slice(0, 5).filter((entry) => entry.score <= 50).length;
  const sustainedDrop = lowStreak >= 5 || (mean7d !== null && mean28d !== null && mean7d < mean28d * 0.85);

  return { mean7d, mean28d, sustainedDrop };
}
